import { registerBlockType } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import { 
    useBlockProps, 
    MediaUploadCheck, 
    MediaUpload, 
    InspectorControls,
    RichText,
    InnerBlocks,
    useInnerBlocksProps
} from '@wordpress/block-editor';
import { 
    PanelBody, 
    Button, 
    RangeControl,
    SelectControl,
    TabPanel
} from '@wordpress/components';
import ResponsiveNumber from '../../components/ResponsiveControl';
import VisibilityControl from '../../components/VisibilityControl';
import FlatColorPicker from '../../components/FlatColorPicker';
import Typography from '../../components/Typography';
import './style.scss';
import './editor.scss';
import { useEffect } from '@wordpress/element';
import { getTypographyStyles } from '../../utils/styles';

// Front-end için numaralandırma scripti
const galleryNumberingScript = `
    document.addEventListener('DOMContentLoaded', function() {
        const updateGalleryNumbers = () => {
            const galleries = document.querySelectorAll('.kanews-gallery');
            galleries.forEach(gallery => {
                const items = gallery.querySelectorAll('.gallery-item');
                const totalItems = items.length;
                
                items.forEach((item, index) => {
                    const numberSpan = item.querySelector('.gallery-item-number');
                    const totalSpan = item.querySelector('.gallery-item-total');
                    
                    if (numberSpan && totalSpan) {
                        numberSpan.textContent = index + 1;
                        totalSpan.textContent = '| ' + totalItems;
                    }
                });
            });
        };

        // Sayfa yüklendiğinde ve her değişiklikte numaraları güncelle
        updateGalleryNumbers();
        
        // MutationObserver ile DOM değişikliklerini izle
        const observer = new MutationObserver(updateGalleryNumbers);
        const config = { childList: true, subtree: true };
        
        document.querySelectorAll('.kanews-gallery').forEach(gallery => {
            observer.observe(gallery, config);
        });
    });
`;

// Gallery Item Block
registerBlockType('kanews/gallery-item', {
    title: __('Galeri Öğesi', 'kanews'),
    category: 'kanews',
    parent: ['kanews/gallery'],
    attributes: {
        url: {
            type: 'string',
            source: 'attribute',
            selector: 'img',
            attribute: 'src'
        },
        id: {
            type: 'number',
            source: 'attribute',
            selector: 'img',
            attribute: 'data-id'
        },
        alt: {
            type: 'string',
            source: 'attribute',
            selector: 'img',
            attribute: 'alt'
        },
        title: {
            type: 'string',
            source: 'html',
            selector: '.gallery-item-title'
        },
        titleTag: {
            type: 'string',
            default: 'h3'
        },
        typography: {
            type: 'object',
            default: {
                fontSize: '',
                fontFamily: '',
                fontWeight: '',
                lineHeight: '',
                letterSpacing: '',
                textTransform: 'none',
                fontStyle: 'normal',
                textDecoration: 'none',
                textAlign: 'left',
                color: ''
            }
        }
    },
    edit: ({ attributes, setAttributes, clientId }) => {
        const { url, id, alt, title, titleTag, typography } = attributes;

        const onSelectImage = (image) => {
            setAttributes({
                url: image.url,
                id: image.id,
                alt: image.alt
            });
        };

        const titleStyle = getTypographyStyles(typography);

        return (
            <div className="gallery-item">
                <InspectorControls>
                    <PanelBody title={__('Başlık Ayarları', 'kanews')}>
                        <SelectControl
                            label={__('Başlık Etiketi', 'kanews')}
                            value={titleTag}
                            options={[
                                { label: 'H1', value: 'h1' },
                                { label: 'H2', value: 'h2' },
                                { label: 'H3', value: 'h3' },
                                { label: 'H4', value: 'h4' },
                                { label: 'H5', value: 'h5' },
                                { label: 'H6', value: 'h6' },
                                { label: 'P', value: 'p' }
                            ]}
                            onChange={(value) => setAttributes({ titleTag: value })}
                        />
                        <Typography
                            value={typography}
                            onChange={(value) => setAttributes({ typography: value })}
                            label={__('Başlık Tipografisi', 'kanews')}
                        />
                    </PanelBody>
                </InspectorControls>
                <div className="gallery-item-header">
                    <div className="gallery-item-counter">
                        <span className="gallery-item-number"></span>
                        <span className="gallery-item-total"></span>
                    </div>
                    <MediaUploadCheck>
                        <MediaUpload
                            onSelect={onSelectImage}
                            allowedTypes={['image']}
                            value={id}
                            render={({ open }) => (
                                <Button
                                    onClick={open}
                                    className={!url ? 'components-button is-primary' : 'components-button is-secondary'}
                                >
                                    {!url ? __('Resim Seç', 'kanews') : __('Resmi Değiştir', 'kanews')}
                                </Button>
                            )}
                        />
                    </MediaUploadCheck>
                    <RichText
                        tagName={titleTag}
                        className="gallery-item-title"
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                        placeholder={__('Başlık...', 'kanews')}
                        style={titleStyle}
                    />
                </div>
                {url && (
                    <div className="gallery-item-image">
                        <img src={url} alt={alt} data-id={id} />
                    </div>
                )}
                <div className="gallery-item-content">
                    <InnerBlocks
                        allowedBlocks={['core/paragraph', 'core/heading', 'core/list', 'core/quote', 'core/image']}
                        template={[
                            ['core/paragraph', { placeholder: __('İçerik ekleyin...', 'kanews') }]
                        ]}
                        renderAppender={InnerBlocks.ButtonBlockAppender}
                    />
                </div>
            </div>
        );
    },
    save: ({ attributes }) => {
        const { url, id, alt, title, titleTag, typography } = attributes;

        const titleStyle = getTypographyStyles(typography, true);

        return (
            <div className="gallery-item">
                <div className="gallery-item-header">
                    <div className="gallery-item-counter">
                        <span className="gallery-item-number"></span>
                        <span className="gallery-item-total"></span>
                    </div>
                    <RichText.Content
                        tagName={titleTag}
                        className="gallery-item-title"
                        value={title}
                        style={titleStyle}
                    />
                </div>
                {url && (
                    <div className="gallery-item-image">
                        <img src={url} alt={alt} data-id={id} />
                    </div>
                )}
                <div className="gallery-item-content">
                    <InnerBlocks.Content />
                </div>
            </div>
        );
    }
});

// Main Gallery Block
registerBlockType('kanews/gallery', {
    title: __('Galeri', 'kanews'),
    category: 'kanews',
    icon: 'format-gallery',
    supports: {
        html: false,
        align: ['wide', 'full']
    },
    attributes: {
        columnsDesktop: {
            type: 'number',
            default: 1
        },
        columnsTablet: {
            type: 'number',
            default: 1
        },
        columnsMobile: {
            type: 'number',
            default: 1
        },
        gridGap: {
            type: 'string',
            default: '1rem'
        },
        imageHeight: {
            type: 'string',
            default: 'auto'
        },
        headerBackground: {
            type: 'string',
            default: ''
        },
        contentBackground: {
            type: 'string',
            default: ''
        },
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        }
    },
    edit: ({ attributes, setAttributes }) => {
        const blockProps = useBlockProps();
        const { 
            columnsDesktop, 
            columnsTablet, 
            columnsMobile, 
            gridGap, 
            imageHeight,
            headerBackground,
            contentBackground,
            visibility
        } = attributes;

        const gridStyle = {
            '--columns-desktop': columnsDesktop.toString(),
            '--columns-tablet': columnsTablet.toString(),
            '--columns-mobile': columnsMobile.toString(),
            '--gap': gridGap,
            '--image-height': imageHeight,
            '--kan-placeholder-bg': headerBackground,
            '--kan-bg-white-color': contentBackground
        };

        const updateItemNumbers = () => {
            const galleryItems = document.querySelectorAll('.gallery-item');
            const totalItems = galleryItems.length;
            
            galleryItems.forEach((item, index) => {
                const numberSpan = item.querySelector('.gallery-item-number');
                const totalSpan = item.querySelector('.gallery-item-total');
                
                if (numberSpan && totalSpan) {
                    numberSpan.textContent = `${index + 1}`;
                    totalSpan.textContent = `| ${totalItems}`;
                }
            });
        };

        // İlk yüklemede ve her değişiklikte numaraları güncelle
        useEffect(() => {
            updateItemNumbers();
        });

        const tabs = [
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        return (
            <div {...blockProps}>
                <InspectorControls>
                    <TabPanel
                        className="kanews-tab-panel"
                        tabs={tabs}
                    >
                        {(tab) => {
                            return (
                                <>
                                    {tab.name === 'design' && (
                                        <PanelBody>
                                            <ResponsiveNumber
                                                value={{
                                                    desktop: columnsDesktop,
                                                    tablet: columnsTablet,
                                                    mobile: columnsMobile
                                                }}
                                                onChange={(values) => {
                                                    setAttributes({
                                                        columnsDesktop: values.desktop,
                                                        columnsTablet: values.tablet,
                                                        columnsMobile: values.mobile
                                                    });
                                                }}
                                                min={1}
                                                max={6}
                                                defaultValues={{
                                                    desktop: 3,
                                                    tablet: 2,
                                                    mobile: 1
                                                }}
                                            />
                                            <SelectControl
                                                label={__('Grid Boşluğu', 'kanews')}
                                                value={gridGap}
                                                options={[
                                                    { label: 'Küçük', value: '0.5rem' },
                                                    { label: 'Normal', value: '1rem' },
                                                    { label: 'Büyük', value: '2rem' }
                                                ]}
                                                onChange={(value) => setAttributes({ gridGap: value })}
                                            />
                                            <SelectControl
                                                label={__('Resim Yüksekliği', 'kanews')}
                                                value={imageHeight}
                                                options={[
                                                    { label: 'Orijinal', value: 'auto' },
                                                    { label: 'Küçük', value: '150px' },
                                                    { label: 'Normal', value: '200px' },
                                                    { label: 'Büyük', value: '300px' }
                                                ]}
                                                onChange={(value) => setAttributes({ imageHeight: value })}
                                            />
                                            <div className="components-base-control">
                                                <label className="components-base-control__label">
                                                    {__('Header Arkaplan Rengi', 'kanews')}
                                                </label>
                                                <FlatColorPicker
                                                    value={headerBackground}
                                                    onChange={(value) => setAttributes({ headerBackground: value })}
                                                />
                                            </div>
                                            <div className="components-base-control">
                                                <label className="components-base-control__label">
                                                    {__('İçerik Arkaplan Rengi', 'kanews')}
                                                </label>
                                                <FlatColorPicker
                                                    value={contentBackground}
                                                    onChange={(value) => setAttributes({ contentBackground: value })}
                                                />
                                            </div>
                                        </PanelBody>
                                    )}

                                    {tab.name === 'visibility' && (
                                        <PanelBody>
                                            <VisibilityControl
                                                value={visibility}
                                                onChange={(value) => setAttributes({ visibility: value })}
                                            />
                                        </PanelBody>
                                    )}
                                </>
                            );
                        }}
                    </TabPanel>
                </InspectorControls>

                <div 
                    className={`kanews-gallery ${visibility.desktop ? 'hidden-desktop' : ''} ${visibility.tablet ? 'hidden-tablet' : ''} ${visibility.mobile ? 'hidden-mobile' : ''}`} 
                    style={gridStyle}
                >
                    <div className="gallery-grid">
                        <InnerBlocks
                            allowedBlocks={['kanews/gallery-item']}
                            template={[
                                ['kanews/gallery-item']
                            ]}
                            renderAppender={InnerBlocks.ButtonBlockAppender}
                            onChange={() => {
                                setTimeout(updateItemNumbers, 0);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    },
    save: ({ attributes }) => {
        const blockProps = useBlockProps.save();
        const { 
            columnsDesktop, 
            columnsTablet, 
            columnsMobile, 
            gridGap, 
            imageHeight,
            headerBackground,
            contentBackground,
            visibility
        } = attributes;

        const gridStyle = {
            '--columns-desktop': columnsDesktop.toString(),
            '--columns-tablet': columnsTablet.toString(),
            '--columns-mobile': columnsMobile.toString(),
            '--gap': gridGap,
            '--image-height': imageHeight,
            '--kan-placeholder-bg': headerBackground,
            '--kan-bg-white-color': contentBackground
        };

        return (
            <>
                <div {...blockProps}>
                    <div 
                        className={`kanews-gallery ${visibility.desktop ? 'hidden-desktop' : ''} ${visibility.tablet ? 'hidden-tablet' : ''} ${visibility.mobile ? 'hidden-mobile' : ''}`} 
                        style={gridStyle}
                    >
                        <div className="gallery-grid">
                            <InnerBlocks.Content />
                        </div>
                    </div>
                </div>
                <script dangerouslySetInnerHTML={{ __html: galleryNumberingScript }} />
            </>
        );
    }
}); 